import { SearchOutlined } from '@ant-design/icons';
import { Checkbox, Flex, Input, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ContentType } from '@pages/Clients/NewClientsList/components/MultiFilter/types';

import { useNewClientsListContext } from '@pages/Clients/NewClientsList/context/useNewClientsListContext';
import { PF } from '@pages/Clients/NewClientsList/data/constants';
import { FilterWrapper } from '@pages/Clients/NewClientsList/ui/FilterWrapper';

const { Text } = Typography;

type CheckboxOption = {
  label?: React.ReactNode;
  value: number;
  title?: string;
};

const normalizeSearch = (value: string) => value.trim().toLowerCase();

const getOptionValuesSet = (options: CheckboxOption[]) =>
  new Set(options.map((option) => option.value));

const intersectSelectedWithOptions = (
  selected: number[],
  options: CheckboxOption[]
): number[] => {
  const allowed = getOptionValuesSet(options);
  return selected.filter((id) => allowed.has(id));
};

const mergeGroupSelection = (
  prevSelected: number[],
  groupOptions: CheckboxOption[],
  nextGroupValues: number[]
): number[] => {
  const groupValueSet = getOptionValuesSet(groupOptions);

  const prevWithoutGroup = prevSelected.filter((id) => !groupValueSet.has(id));
  const nextInGroup = nextGroupValues
    .map((value) => Number(value))
    .filter((id) => groupValueSet.has(id));

  return [...prevWithoutGroup, ...nextInGroup];
};

export const EmployeeFilter: ContentType = ({ open, onClose, id }) => {
  const [localSearch, setLocalSearch] = useState('');
  const [localSelectedEmployees, setLocalSelectedEmployees] = useState<
    number[]
  >([]);

  const {
    filtersMap,
    onChangeEmployees,
    activeEmployeesOptions,
    firedEmployeesOptions,
  } = useNewClientsListContext();

  const { employees } = filtersMap.get(id)!;

  const onConfirm = useCallback(() => {
    onChangeEmployees(localSelectedEmployees, id);
    onClose?.();
  }, [id, localSelectedEmployees, onChangeEmployees, onClose]);

  useEffect(() => {
    if (open) {
      setLocalSelectedEmployees(employees);
      return;
    }
    setLocalSelectedEmployees([]);
    setLocalSearch('');
  }, [employees, open]);

  const filteredActiveEmployeesOptions = useMemo(() => {
    const search = normalizeSearch(localSearch);
    if (!search) return activeEmployeesOptions;

    return activeEmployeesOptions.filter((option) =>
      (option?.title ?? '').toLowerCase().includes(search)
    );
  }, [activeEmployeesOptions, localSearch]);

  const filteredFiredEmployeesOptions = useMemo(() => {
    const search = normalizeSearch(localSearch);
    if (!search) return firedEmployeesOptions;

    return firedEmployeesOptions.filter((option) =>
      (option?.title ?? '').toLowerCase().includes(search)
    );
  }, [firedEmployeesOptions, localSearch]);

  const activeGroupValue = useMemo(
    () =>
      intersectSelectedWithOptions(
        localSelectedEmployees,
        filteredActiveEmployeesOptions
      ),
    [filteredActiveEmployeesOptions, localSelectedEmployees]
  );

  const firedGroupValue = useMemo(
    () =>
      intersectSelectedWithOptions(
        localSelectedEmployees,
        filteredFiredEmployeesOptions
      ),
    [filteredFiredEmployeesOptions, localSelectedEmployees]
  );

  const activeChangeHandler = useCallback(
    (checkedValues: number[]) => {
      setLocalSelectedEmployees((prev) =>
        mergeGroupSelection(prev, filteredActiveEmployeesOptions, checkedValues)
      );
    },
    [filteredActiveEmployeesOptions]
  );

  const firedChangeHandler = useCallback(
    (checkedValues: number[]) => {
      setLocalSelectedEmployees((prev) =>
        mergeGroupSelection(prev, filteredFiredEmployeesOptions, checkedValues)
      );
    },
    [filteredFiredEmployeesOptions]
  );

  return (
    <FilterWrapper onConfirm={onConfirm}>
      <Flex vertical gap={8}>
        <Input
          placeholder="Поиск сотрудника"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          prefix={<SearchOutlined />}
        />

        <Text strong>Действующие сотрудники:</Text>
        <div className={`${PF}__tags-container`}>
          <div className={`${PF}__tags-list`}>
            <Checkbox.Group<number>
              options={filteredActiveEmployeesOptions}
              value={activeGroupValue}
              onChange={activeChangeHandler}
              className={`${PF}__checkbox-group`}
            />
          </div>
        </div>

        <Text strong>Уволенные сотрудники:</Text>
        <div className={`${PF}__tags-container`}>
          <div className={`${PF}__tags-list`}>
            <Checkbox.Group<number>
              options={filteredFiredEmployeesOptions}
              value={firedGroupValue}
              onChange={firedChangeHandler}
              className={`${PF}__checkbox-group`}
            />
          </div>
        </div>
      </Flex>
    </FilterWrapper>
  );
};
